import { safeLocalStorage } from '@/lib/safe-storage'

// API Configuration
export const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.codedesign.ai'
export const BUILDER_URL = (import.meta.env.VITE_BASE_BUILDER_URL || 'https://dev.codedesign.ai').replace(/\/+$/, '')
export const DEV_URL = BUILDER_URL // Backward compat alias
export const DRAFT_AI_GENERATIONS_API = (
    import.meta.env.VITE_API_BASE_URL    
).replace(/\/+$/, '')
export const AI_COMPANION_API = (import.meta.env.VITE_AI_COMPANION_API_URL || '').replace(/\/+$/, '')
export const AI_COMPANION_PLAYGROUND_API = (
    import.meta.env.VITE_AI_COMPANION_PLAYGROUND_API_URL || 'https://rahul-dev-api.codedesign.app'
).replace(/\/+$/, '')
export const ACCOUNTS_URL = import.meta.env.VITE_ACCOUNTS_URL || 'https://accounts.codedesign.ai'
export const LEGACY_BUILDER_BASE = import.meta.env.VITE_LEGACY_BUILDER_BASE || `${BUILDER_URL}/app/builder`
export const LEGACY_PREVIEW_BUILDER_BASE = import.meta.env.VITE_LEGACY_PREVIEW_BUILDER_BASE || `${BUILDER_URL}/page-preview`

export const API_BETA = import.meta.env.VITE_API_BETA_URL || 'https://geethu-dev-api.codedesign.app/'

// Calendar API base. No trailing slash; paths added at the call site begin with `/`.
// Set VITE_CALENDAR_API_BASE in .env (e.g. "https://apps-development-api.codedesign.app" or "http://localhost:4040").
export const CALENDAR_API_BASE = (import.meta.env.VITE_CALENDAR_API_BASE || '').replace(/\/+$/, '')

// Public booking page (the separate Vite app deployed at calendar.codedesign.ai).
// Used to build shareable booking links + embed snippets shown in the dashboard.
export const CALENDAR_PUBLIC_URL = (import.meta.env.VITE_CALENDAR_PUBLIC_URL || 'https://calendar.codedesign.ai').replace(/\/+$/, '')

// Cookie consent authoring API and public runtime host.
export const COOKIE_CONSENT_API_URL = (import.meta.env.VITE_COOKIE_CONSENT_API_URL || 'https://apps-development-api.codedesign.app').replace(/\/+$/, '')
export const COOKIE_CONSENT_RUNTIME_URL = (import.meta.env.VITE_COOKIE_CONSENT_RUNTIME_URL || 'https://cookies.scripts.codedesign.ai').replace(/\/+$/, '')

// Form Builder authoring API and the independent respondent runtime.
export const FORM_BUILDER_API_BASE = (import.meta.env.VITE_FORM_BUILDER_API_BASE || 'http://localhost:4040').replace(/\/+$/, '')
export const FORMS_FRONTEND_URL = (import.meta.env.VITE_FORMS_FRONTEND_URL || 'http://localhost:5175').replace(/\/+$/, '')

// SEO Audit API - runs on port 3005 locally, or use the production domain
// Set VITE_SEO_AUDIT_API_URL in .env to override
export const SEO_AUDIT_API = import.meta.env.VITE_SEO_AUDIT_API_URL || 'https://site-audit-api.codedesign.app/'
/** Blog backend base URL (blogs, tags, setup, templates). Upload/AI use API_BETA. */
export const BLOG_API_BASE = 'https://production-api.codedesign.ai/api/'

export const DIRECTORY_SUBMISSION_ADDON_CTA_LINK = 'https://buy.stripe.com/9B64gy8fPdfwgXTdIUf7i0z'
export const HUMAN_SERVICE_ADDON_CTA_LINK = 'https://buy.stripe.com/00wcN4bs15N4azv0W8f7i0A'

// Default values
export const DEFAULT_WORKSPACE = 'default'

// Routes
export const ROUTES = {
    // Main dashboard
    HOME: '/',
    WORKSPACE: '/',

    // Features
    SITE_PAGES: '/pages',
    COMPONENTS: '/components',
    BLOCKS: '/blocks',
    BLOG: '/blog',
    FORMS: '/forms',
    CONTACTS: '/contacts',
    NOTIFICATIONS: '/notifications',
    WEBHOOKS: '/webhooks',
    INTEGRATIONS: '/integrations',
    AI_CHATBOT: '/chatbot',
    COOKIE_MANAGER: '/cookie-manager',
    DOMAINS: '/domains',
    SEO: '/seo',
    SITE_SPEED: '/site-speed',
    SETTINGS: '/settings',
    TEAM: '/team',
}

// Navigation items for sidebar
export const NAVIGATION = [
    {
        title: 'Overview',
        href: ROUTES.HOME,
        icon: 'home',
    },
    {
        title: 'Site Pages',
        items: [
            { title: 'Pages', href: ROUTES.SITE_PAGES, icon: 'file' },
            { title: 'Components', href: ROUTES.COMPONENTS, icon: 'component' },
            { title: 'Blocks', href: ROUTES.BLOCKS, icon: 'grid' },
        ],
    },
    {
        title: 'Content',
        items: [
            { title: 'Blog', href: ROUTES.BLOG, icon: 'edit' },
        ],
    },
    {
        title: 'Customer & Leads',
        items: [
            { title: 'Forms', href: ROUTES.FORMS, icon: 'form' },
            { title: 'Contacts', href: ROUTES.CONTACTS, icon: 'users' },
            { title: 'Notifications', href: ROUTES.NOTIFICATIONS, icon: 'bell' },
            { title: 'Webhooks', href: ROUTES.WEBHOOKS, icon: 'webhook' },
        ],
    },
    {
        title: 'AI Chatbot',
        href: ROUTES.AI_CHATBOT,
        icon: 'chat',
    },
    {
        title: 'Cookie Manager',
        href: ROUTES.COOKIE_MANAGER,
        icon: 'shield',
    },
    {
        title: 'Integrations',
        href: ROUTES.INTEGRATIONS,
        icon: 'puzzle',
    },
    {
        title: 'Settings',
        items: [
            { title: 'Domains', href: ROUTES.DOMAINS, icon: 'globe' },
            { title: 'SEO & Meta', href: ROUTES.SEO, icon: 'search' },
            { title: 'Site Speed', href: ROUTES.SITE_SPEED, icon: 'zap' },
            { title: 'General', href: ROUTES.SETTINGS, icon: 'settings' },
            { title: 'Team', href: ROUTES.TEAM, icon: 'users' },
        ],
    },
]

// User roles
export const USER_ROLES = {
    OWNER: 'owner',
    ADMIN: 'admin',
    USER: 'user',
}

// API endpoints
export const ENDPOINTS = {
    // Auth
    ME: '/auth/me',

    // Workspaces
    WORKSPACES: '/workspaces',
    WORKSPACE: (id) => `/workspaces/${id}`,

    // Projects
    PROJECTS: (workspaceId) => `/workspaces/${workspaceId}/projects`,
    PROJECT: (id) => `/projects/${id}`,

    // Team
    WORKSPACE_TEAM: (workspaceId) => `/workspaces/${workspaceId}/team`,
    PROJECT_TEAM: (projectId) => `/projects/${projectId}/team`,

    // Features (to be expanded)
    PAGES: (projectId) => `/projects/${projectId}/pages`,
    FORMS: (projectId) => `/projects/${projectId}/forms`,
    BLOG_POSTS: (projectId) => `/projects/${projectId}/blog/posts`,
    CONTACTS: (projectId) => `/projects/${projectId}/contacts`,
    DOMAINS: (projectId) => `/projects/${projectId}/domains`,
}



export const getApiToken = () => safeLocalStorage.getItem('auth_token') || ''

export const getApiGuestToken = () => safeLocalStorage.getItem('guest-token') || ''

export const jsonHeadersWithToken = () => {
    const headers = { 'Content-Type': 'application/json' }
    const token = getApiToken()
    const guestToken = getApiGuestToken()

    if (token) headers['x-access-token'] = token
    if (guestToken) headers['guest-token'] = guestToken

    return headers
}

export const authHeaders = () => {
    const headers = {}
    const token = getApiToken()
    const guestToken = getApiGuestToken()

    if (token) headers['x-access-token'] = token
    if (guestToken) headers['guest-token'] = guestToken

    return headers
}

export const parseJsonSafe = async (response) => {
    try {
        return await response.json()
    } catch {
        return {}
    }
}

export const getErrorMessage = (errorData, fallback) =>
    (typeof errorData?.message === 'string' && errorData.message) ||
    (typeof errorData?.errorMessage === 'string' && errorData.errorMessage) ||
    (typeof errorData?.error === 'string' && errorData.error) ||
    fallback

/**
 * Force logout: clear auth tokens and redirect to accounts login.
 * Use this when the session is invalid (401, deleted user, etc.)
 */
export const forceLogout = () => {
    safeLocalStorage.removeItem('auth_token')
    safeLocalStorage.removeItem('user_data')

    // Clear auth cookies on all known ecosystem domains
    const authCookieNames = ['token', 'userId']
    const knownDomains = [
        '.codedesign.app', '.codedesign.ai',
        'dev.codedesign.app', '.dev.codedesign.app',
        'dev.codedesign.ai', '.dev.codedesign.ai',
        'staging-accounts.codedesign.app', 'staging-production-jqesv.codedesign.app',
        'accounts.codedesign.ai', '.cdsn.me', 'dev.cdsn.me', '.dev.cdsn.me',
        'dashboard.codedesign.ai', '.dashboard.codedesign.ai',
        'dashboard.cdsn.me', '.dashboard.cdsn.me',
    ]
    authCookieNames.forEach((name) => {
        knownDomains.forEach((domain) => {
            document.cookie = `${name}=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`
        })
    })

    window.location.href = `${ACCOUNTS_URL}/login`
}
